"use client";

import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { GlassCard } from "@/src/components/GlassCard";
import { login } from "@/src/actions/auth-session";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/components/ui/utils";
import { Button } from "@/src/components/ui/button";
import { requiredNoWhitespace } from "@/src/utils/validations";
import { useTopLoader } from "nextjs-toploader";

const inputBaseClassName =
  "w-full h-auto pl-12 pr-4 py-3.5 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 focus:ring-2 focus:ring-walnut-500/20 transition-all border-white/30 dark:border-white/10";
const inputWithToggleBaseClassName =
  "w-full h-auto pl-12 pr-12 py-3.5 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 focus:ring-2 focus:ring-walnut-500/20 transition-all border-white/30 dark:border-white/10";
const labelClassName = "text-sm font-semibold text-walnut-900 dark:text-white";

type SignInFormValues = {
  email: string;
  password: string;
  rememberMe: boolean;
};

export const SignInPage: React.FC = () => {
  const { t } = useTranslation("common");
  const searchParams = useSearchParams();
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const { start } = useTopLoader();
  const registeredToastShown = React.useRef(false);

  useEffect(() => {
    if (
      searchParams.get("registered") === "1" &&
      !registeredToastShown.current
    ) {
      registeredToastShown.current = true;
      toast.success(t("auth.signIn.registeredSuccess"));
    }
  }, [searchParams, t]);

  const form = useForm<SignInFormValues>({
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (values: SignInFormValues) => {
    try {
      await login({
        email: values.email.trim(),
        password: values.password.trim(),
      });

      toast.success(t("auth.signIn.success"));
      const next = searchParams.get("next");
      const redirectUrl =
        next &&
        (next.startsWith("/dashboard") ||
          next.startsWith("/studio") ||
          next.startsWith("/diecuts"))
          ? next
          : "/dashboard";
      start();
      router.push(redirectUrl);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("auth.signIn.errors.notACustomer"));
    }
  };

  const benefits = [
    { icon: CheckCircle, text: t("auth.signIn.benefits.savedDesigns") },
    { icon: CheckCircle, text: t("auth.signIn.benefits.orderHistory") },
    { icon: CheckCircle, text: t("auth.signIn.benefits.instantQuotes") },
    { icon: CheckCircle, text: t("auth.signIn.benefits.collaboration") },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="mb-8">
            <h1 className="text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("auth.signIn.title")}
            </h1>
            <p className="text-lg text-walnut-700 dark:text-walnut-300">
              {t("auth.signIn.subtitle")}
            </p>
          </div>

          <div className="space-y-4">
            {benefits.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-walnut-700 dark:text-walnut-300 font-medium">
                  {item.text}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <GlassCard className="p-8 md:p-12">
            <div className="lg:hidden mb-8 text-center">
              <h1 className="text-3xl font-bold text-walnut-900 dark:text-white mb-2">
                {t("auth.signIn.mobileTitle")}
              </h1>
              <p className="text-walnut-700 dark:text-walnut-300">
                {t("auth.signIn.mobileSubtitle")}
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: t("auth.signIn.errors.emailRequired"),
                    validate: requiredNoWhitespace(
                      t("auth.signIn.errors.emailRequired"),
                    ),
                  }}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className={labelClassName}>
                        {t("auth.signIn.email")}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                          <Input
                            {...field}
                            type="email"
                            placeholder={t("auth.signIn.emailPlaceholder")}
                            className={cn(
                              inputBaseClassName,
                              fieldState?.error &&
                                "border-destructive focus:border-destructive focus:ring-destructive/20",
                            )}
                            aria-invalid={!!fieldState?.error}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  rules={{
                    required: t("auth.signIn.errors.passwordRequired"),
                    validate: requiredNoWhitespace(
                      t("auth.signIn.errors.passwordRequired"),
                    ),
                  }}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className={labelClassName}>
                        {t("auth.signIn.password")}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400 pointer-events-none" />
                          <Input
                            {...field}
                            type={showPassword ? "text" : "password"}
                            placeholder={t("auth.signIn.passwordPlaceholder")}
                            className={cn(
                              inputWithToggleBaseClassName,
                              fieldState?.error &&
                                "border-destructive focus:border-destructive focus:ring-destructive/20",
                            )}
                            aria-invalid={!!fieldState?.error}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-between">
                  <FormField
                    control={form.control}
                    name="rememberMe"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            className="w-5 h-5 rounded-lg border-2 border-white/30 dark:border-white/10 bg-white/20 dark:bg-black/20 checked:bg-gradient-to-br checked:from-walnut-600 checked:to-walnut-700 checked:border-walnut-600 focus:outline-none focus:ring-2 focus:ring-walnut-500/20 transition-all cursor-pointer"
                          />
                        </FormControl>
                        <FormLabel className="mt-0 cursor-pointer text-sm text-walnut-700 dark:text-walnut-300 font-normal">
                          {t("auth.signIn.rememberMe")}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <Link
                    href="/forgot-password"
                    className="text-sm text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-medium transition-colors"
                  >
                    {t("auth.signIn.forgotPassword")}
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  pill
                  loading={form.formState.isSubmitting}
                  className="w-full"
                >
                  {t("auth.signIn.signInButton")}
                  <ArrowRight className="w-5 h-5" />
                </Button>

                <div className="text-center">
                  <span className="text-walnut-700 dark:text-walnut-300">
                    {t("auth.signIn.noAccount")}{" "}
                  </span>
                  <Link
                    href={`/signup${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next")!)}` : ""}`}
                    className="text-walnut-600 dark:text-walnut-400 hover:text-walnut-800 dark:hover:text-walnut-200 font-semibold transition-colors"
                  >
                    {t("auth.signIn.signUpLink")}
                  </Link>
                </div>
              </form>
            </Form>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
